/**
 * Rate Signature Hash Utility (Browser-compatible)
 *
 * Generates a deterministic hash/signature from rate's important fields
 * for duplicate detection during import. Unlike cargofiveId which is source-specific,
 * this signature is based on the rate content itself.
 */

/**
 * Normalize a string value for consistent hashing
 */
function normalizeString(val) {
    if (val === null || val === undefined) return '';
    return String(val).trim().toLowerCase();
}

/**
 * Normalize a port/location object to a consistent string representation
 */
function normalizePort(port) {
    if (!port) return '';
    return `${normalizeString(port.type)}:${normalizeString(port.id)}:${normalizeString(port.countryCode)}`;
}

/**
 * Normalize product object to a consistent sorted representation
 */
function normalizeProduct(product) {
    if (!product) return '';
    const sortedKeys = Object.keys(product).sort();
    const parts = [];
    for (const key of sortedKeys) {
        if (key === 'id') continue;
        const val = product[key];
        if (val === null || val === undefined) continue;
        if (Array.isArray(val)) {
            parts.push(`${key}:${val.slice().sort().join(',')}`);
        } else if (typeof val === 'object') {
            parts.push(`${key}:{${normalizeProduct(val)}}`);
        } else {
            parts.push(`${key}:${normalizeString(val)}`);
        }
    }
    return parts.join('|');
}

/**
 * Normalize a field value object
 */
function normalizeFieldValue(valueObj) {
    if (!valueObj) return '';
    const parts = [];
    if (valueObj.value !== undefined && valueObj.value !== null) {
        parts.push(`v:${valueObj.value}`);
    }
    if (valueObj.formula) {
        parts.push(`f:${normalizeString(valueObj.formula)}`);
    }
    if (valueObj.currency) {
        parts.push(`c:${normalizeString(valueObj.currency)}`);
    }
    if (valueObj.multiplier !== undefined && valueObj.multiplier !== null) {
        parts.push(`m:${valueObj.multiplier}`);
    }
    return parts.sort().join(',');
}

/**
 * Normalize a single field for hashing
 */
function normalizeField(field, productId) {
    if (!field) return '';
    const parts = [
        `title:${normalizeString(field.title)}`,
        `type:${normalizeString(field.type)}`,
    ];

    if (field.values && typeof field.values === 'object') {
        const valueKeys = Object.keys(field.values).sort();
        for (const vk of valueKeys) {
            parts.push(`val[${vk === productId ? 'product' : vk}]:${normalizeFieldValue(field.values[vk])}`);
        }
    }

    return parts.join('|');
}

/**
 * Normalize an offer within a section
 */
function normalizeOffer(offer, productId) {
    if (!offer) return '';
    const parts = [];

    const fields = (offer.fields || [])
        .map(f => ({ ...f, _normalized: normalizeField(f, productId) }))
        .sort((a, b) => a._normalized.localeCompare(b._normalized));

    for (const field of fields) {
        parts.push(field._normalized);
    }

    return parts.join('||');
}

/**
 * Normalize a section for hashing
 */
function normalizeSection(section, productId) {
    if (!section) return '';
    const parts = [
        `title:${normalizeString(section.title)}`,
    ];

    const offers = (section.offers || [])
        .map(o => normalizeOffer(o, productId))
        .sort();

    for (const offer of offers) {
        parts.push(`offer:{${offer}}`);
    }

    return parts.join('|');
}

/**
 * Normalize all sections for hashing
 */
function normalizeSections(sections, productId) {
    if (!sections || !Array.isArray(sections)) return '';

    const normalizedSections = sections
        .map(s => normalizeSection(s, productId))
        .sort();

    return normalizedSections.join('|||');
}

/**
 * Normalize a timestamp to a date string (YYYY-MM-DD) for consistency
 */
function normalizeDate(timestamp) {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return normalizeString(timestamp);
    return date.toISOString().split('T')[0];
}

/**
 * Build the normalized string input for signature generation
 * This is separated so it can be used synchronously for comparison
 */
export function buildSignatureInput(rate) {
    if (!rate) return null;

    const components = [];

    components.push(`src:${normalizePort(rate.source)}`);
    components.push(`dst:${normalizePort(rate.destination)}`);
    components.push(`product:${normalizeProduct(rate.product)}`);

    const supplierOrg = rate.supplier?.organization || '';
    components.push(`supplier:${normalizeString(supplierOrg)}`);

    const validFrom = rate.offer?.validFrom || '';
    const validUntil = rate.offer?.validUntil || '';
    components.push(`validFrom:${normalizeDate(validFrom)}`);
    components.push(`validUntil:${normalizeDate(validUntil)}`);

    components.push(`type:${normalizeString(rate.type)}`);

    const transitTime = rate.offer?.transitTime;
    components.push(`transitTime:${transitTime !== undefined && transitTime !== null ? transitTime : ''}`);

    const transshipment = rate.offer?.transshipment || '';
    components.push(`transshipment:${normalizeString(transshipment)}`);

    const sections = rate.offer?.sections || [];
    components.push(`sections:${normalizeSections(sections, rate.product?.id)}`);

    return components.join('\n');
}

/**
 * Generate a deterministic signature hash for a rate (async, uses Web Crypto API)
 *
 * @param {Object} rate - The rate object
 * @returns {Promise<string>} - SHA256 hash of the normalized rate data
 */
export async function generateRateSignature(rate) {
    const signatureInput = buildSignatureInput(rate);
    if (!signatureInput) return null;

    // Use Web Crypto API for browser compatibility
    const encoder = new TextEncoder();
    const data = encoder.encode(signatureInput);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // Convert to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
}

/**
 * Generate signatures for multiple rates in parallel
 *
 * @param {Object[]} rates - Array of rate objects
 * @returns {Promise<Map<string, string>>} - Map of rate.id -> signature
 */
export async function generateRateSignatures(rates) {
    const results = await Promise.all(
        rates.map(async (rate) => {
            const signature = await generateRateSignature(rate);
            return [rate.id || rate.attributes?.cargofiveId, signature];
        })
    );

    return new Map(results.filter(([, sig]) => sig !== null));
}

export default {
    generateRateSignature,
    generateRateSignatures,
    buildSignatureInput,
};
